import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import { useParams } from "react-router-dom";

import SideBarForRole7 from "../components/sidebar/SideBarForRole7";
import NavbarForRole7 from "../components/navbar/NavbarForRole7";
import BodyComposition from "../components/bodyComposition";
import FetchDataButton from "../components/fetchDataButton";
import { METRICS, getMetric } from "../config/metrics";
import "./dashboard.scss";

const OPEN_METRICS_KEY = "openMetrics:v2";

/** Cards are `{ id, type, title }`; only `type` and `title` are persisted. */
const createCard = (type, title, seed) => ({
  id: `${type}:${seed ?? Date.now()}`,
  type,
  title: title ?? getMetric(type)?.label ?? type,
});

const Dashboard = () => {
  const { watchId } = useParams();
  const [cards, setCards] = useState([]);
  const [restored, setRestored] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeCardId, setActiveCardId] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");

  /* --- Persistence ------------------------------------------------------ */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(OPEN_METRICS_KEY);
      const items = raw ? JSON.parse(raw) : [];
      if (Array.isArray(items)) {
        setCards(
          items
            // Drop anything whose metric no longer exists rather than
            // rendering an empty card for it.
            .filter((item) => getMetric(item?.type))
            .map((item, index) => createCard(item.type, item.title, index + 1))
        );
      }
    } catch (error) {
      console.warn("Failed to restore dashboard metrics:", error);
    } finally {
      setRestored(true);
    }
  }, []);

  useEffect(() => {
    if (!restored) return;
    try {
      localStorage.setItem(
        OPEN_METRICS_KEY,
        JSON.stringify(cards.map(({ type, title }) => ({ type, title })))
      );
    } catch (error) {
      console.warn("Failed to persist dashboard metrics:", error);
    }
  }, [cards, restored]);

  /* --- Actions ---------------------------------------------------------- */
  const openPicker = useCallback(() => setPickerOpen(true), []);

  const addMetric = (metricId) => {
    setCards((prev) => [...prev, createCard(metricId)]);
    setPickerOpen(false);
  };

  const activeCard = useMemo(
    () => cards.find((card) => card.id === activeCardId) ?? null,
    [cards, activeCardId]
  );

  const closeMenu = () => {
    setMenuAnchor(null);
    setActiveCardId(null);
  };

  const startRename = () => {
    setRenameValue(activeCard?.title ?? "");
    setRenameOpen(true);
    setMenuAnchor(null); // keep activeCardId so the rename knows its target
  };

  const applyRename = () => {
    const next = renameValue.trim();
    setCards((prev) =>
      prev.map((card) =>
        card.id === activeCardId ? { ...card, title: next || card.title } : card
      )
    );
    setRenameOpen(false);
    setActiveCardId(null);
  };

  const removeCard = () => {
    setCards((prev) => prev.filter((card) => card.id !== activeCardId));
    closeMenu();
  };

  const moveCard = (offset) => {
    setCards((prev) => {
      const index = prev.findIndex((card) => card.id === activeCardId);
      const target = index + offset;
      if (index < 0 || target < 0 || target >= prev.length) return prev;
      const next = prev.slice();
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    closeMenu();
  };

  const activeIndex = cards.findIndex((card) => card.id === activeCardId);

  return (
    <div className="wg-shell">
      <SideBarForRole7 />

      <div className="wg-main">
        <NavbarForRole7 onAddMetric={openPicker} />

        <main className="wg-content">
          <BodyComposition />

          <div className="wg-page-head">
            <div className="wg-page-head__text">
              <p className="wg-eyebrow">Live metrics</p>
              <h1 className="wg-title">Dashboard</h1>
              <p className="wg-subtitle">
                {cards.length
                  ? `${cards.length} metric${cards.length === 1 ? "" : "s"} pinned for this watch.`
                  : "Pin the metrics you want to watch. Your selection is remembered on this device."}
              </p>
            </div>
            <div className="wg-page-head__actions">
              <Button variant="contained" startIcon={<AddIcon />} onClick={openPicker}>
                Add metric
              </Button>
            </div>
          </div>

          <div className="dashboard-grid">
            {cards.map((card) => {
              const metric = getMetric(card.type);
              if (!metric) return null;

              return (
                <div className="dashboard-card" key={card.id}>
                  {metric.render({
                    title: card.title,
                    // Both slots live in the panel's own header, so the card
                    // stays a single aligned row instead of stacking controls
                    // above and below the chart.
                    extraActions:
                      metric.sensorCode === undefined ? null : (
                        <FetchDataButton
                          sensorCode={metric.sensorCode}
                          label="Request"
                        />
                      ),
                    menu: (
                      <IconButton
                        size="small"
                        aria-label={`Options for ${card.title}`}
                        onClick={(event) => {
                          setMenuAnchor(event.currentTarget);
                          setActiveCardId(card.id);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    ),
                  })}
                </div>
              );
            })}

            <button type="button" className="add-metric-card" onClick={openPicker}>
              <span className="add-metric-card__icon">
                <DashboardCustomizeOutlinedIcon />
              </span>
              <span className="add-metric-card__label">Add a metric</span>
              <span className="add-metric-card__hint">
                {METRICS.length} sensors available on this watch
              </span>
            </button>
          </div>
        </main>
      </div>

      {/* --- Metric picker -------------------------------------------- */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle component="div">
          <h2 className="wg-card__title">Add a metric</h2>
          <p className="wg-card__sub">
            Watch <span className="wg-mono">{watchId}</span>
          </p>
        </DialogTitle>
        <DialogContent dividers>
          <div className="metric-picker">
            {METRICS.map((metric) => (
              <button
                type="button"
                key={metric.id}
                className="metric-picker__option"
                onClick={() => addMetric(metric.id)}
              >
                <span
                  className="wg-icon-badge wg-icon-badge--sm"
                  style={{ "--badge-color": `var(${metric.colorToken})` }}
                >
                  {metric.icon}
                </span>
                <span className="metric-picker__option-text">
                  <span>{metric.label}</span>
                  <span className="metric-picker__option-hint">{metric.hint}</span>
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* --- Card menu ------------------------------------------------- */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={startRename}>Rename card</MenuItem>
        <MenuItem onClick={() => moveCard(-1)} disabled={activeIndex <= 0}>
          Move up
        </MenuItem>
        <MenuItem
          onClick={() => moveCard(1)}
          disabled={activeIndex < 0 || activeIndex >= cards.length - 1}
        >
          Move down
        </MenuItem>
        <MenuItem onClick={removeCard}>Remove card</MenuItem>
      </Menu>

      {/* --- Rename ---------------------------------------------------- */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename card</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Display name"
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && applyRename()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyRename}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Dashboard;
